import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';

interface CommentLabel {
  label: string;
  sentiment: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE';
}

interface LocationLabelAggregate {
  _id: string;
  allLabels: CommentLabel[];
  numberOfPositives: number;
  numberOfNeutrals: number;
  numberOfNegatives: number;
  numberOfLabels: number;
}

interface BubbleNode extends LocationLabelAggregate, d3.SimulationNodeDatum {
  radius: number;
}

interface Props {
  data: LocationLabelAggregate[];
}

const SentimentBubbleCloud: React.FC<Props> = ({ data }) => {
  const [nodes, setNodes] = useState<BubbleNode[]>([]);
  const width = 800;
  const height = 600;

  const colors = {
    positive: '#4caf50',
    neutral: '#faad14',
    negative: '#f44336'
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    const maxLabels = d3.max(data, d => d.numberOfLabels) || 1;
    const sizeScale = d3.scaleSqrt()
      .domain([0, maxLabels])
      .range([35, 90]);

    const simulationData: BubbleNode[] = data.map(d => ({
      ...d,
      radius: sizeScale(d.numberOfLabels),
      x: width / 2,
      y: height / 2
    }));

    const simulation = d3.forceSimulation<BubbleNode>(simulationData)
      .force("x", d3.forceX(width / 2).strength(0.2))
      .force("y", d3.forceY(height / 2).strength(0.2))
      .force("collide", d3.forceCollide<BubbleNode>(d => d.radius + 10))
      .on("tick", () => {
        simulationData.forEach(node => {
          node.x = Math.max(node.radius, Math.min(width - node.radius, node.x || 0));
          node.y = Math.max(node.radius, Math.min(height - node.radius, node.y || 0));
        });
        setNodes([...simulationData]);
      });

    return () => {
      simulation.stop();
    };
  }, [data]);

  return (
    <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        background: 'transparent' 
    }}>
      <svg 
        width="100%" 
        height="100%" 
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}
      >
        {nodes.map((node, i) => {
          const total = node.numberOfLabels || 1;
          const posPct = (node.numberOfPositives / total) * 100;
          const neuPct = (node.numberOfNeutrals / total) * 100;
          const negPct = (node.numberOfNegatives / total) * 100;

          return (
            <g key={`${node._id}-${i}`} transform={`translate(${node.x}, ${node.y})`}>
              {/* Círculo de fondo blanco */}
<circle 
  r={node.radius} 
  fill="transparent" 
  style={{ filter: 'drop-shadow(0px 4px 6px rgba(0,0,0,0.1))' }} 
/>

{/* Positivos: Solo si hay más de 0 */}
              {posPct > 0 && (
                <circle
                  r={node.radius - 8}
                  fill="transparent"
                  stroke={colors.positive}
                  strokeWidth="7"
                  strokeDasharray={`${posPct} 100`}
                  pathLength="100"
                  transform="rotate(-90)"
                  strokeLinecap="round"
                />
              )}

              {/* Neutros: Solo si hay más de 0 */}
              {neuPct > 0 && (
                <circle
                  r={node.radius - 8}
                  fill="transparent"
                  stroke={colors.neutral}
                  strokeWidth="7"
                  strokeDasharray={`${neuPct} 100`}
                  strokeDashoffset={-posPct}
                  pathLength="100"
                  transform="rotate(-90)"
                  strokeLinecap="round"
                />
              )}

              {/* Negativos: Solo si hay más de 0 */}
              {negPct > 0 && (
                <circle
                  r={node.radius - 8}
                  fill="transparent"
                  stroke={colors.negative}
                  strokeWidth="7"
                  strokeDasharray={`${negPct} 100`}
                  strokeDashoffset={-(posPct + neuPct)}
                  pathLength="100"
                  transform="rotate(-90)"
                  strokeLinecap="round"
                />
              )}

              {/* Etiquetas de Texto */}
              <text
                textAnchor="middle"
                dy="-0.2em"
                style={{ 
                    // Lógica: A menor radio o mayor longitud de texto, menor fuente
                    fontSize: Math.min(
                      node.radius / 4.5, 
                      (node.radius * 2) / (node._id.length * 0.9)
                    ), 
                    fontWeight: 600, 
                    fill: 'MidnightBlue',
                    fontFamily: 'sans-serif',
                    pointerEvents: 'none'
                }}
              >
                {/* Truncar si es excesivamente largo (ej. más de 20 caracteres) */}
                {node._id.length > 20 ? `${node._id.substring(0, 17)}...` : node._id}
              </text>

              <text
                textAnchor="middle"
                dy="1.1em"
                style={{ 
                    fontSize: Math.max(node.radius / 6.5, 12), 
                    fill: 'slategrey',
                    fontWeight: 600,
                    fontFamily: 'sans-serif',
                    pointerEvents: 'none'
                }}
              >
                x{node.numberOfLabels} 
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default SentimentBubbleCloud;